import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'

const exportDestinations = [
  { country: 'China', volumeMT: 89280, pct: 48, valueMillion: 119 },
  { country: 'Canada', volumeMT: 33480, pct: 18, valueMillion: 45 },
  { country: 'Japan', volumeMT: 16740, pct: 9, valueMillion: 22 },
  { country: 'Denmark', volumeMT: 14880, pct: 8, valueMillion: 20 },
  { country: 'Others', volumeMT: 31620, pct: 17, valueMillion: 42 },
]

const historicalExports = [
  { year: 2019, valueMillion: 198, volumeMT: 145000 },
  { year: 2020, valueMillion: 178, volumeMT: 132000 },
  { year: 2021, valueMillion: 215, volumeMT: 158000 },
  { year: 2022, valueMillion: 231, volumeMT: 162000 },
  { year: 2023, valueMillion: 244, volumeMT: 175000 },
  { year: 2024, valueMillion: 248, volumeMT: 186000 },
]

const pricePerFish = [
  { year: 2019, cents: 5.8 },
  { year: 2020, cents: 5.9 },
  { year: 2021, cents: 6.2 },
  { year: 2022, cents: 6.8 },
  { year: 2023, cents: 7.1 },
  { year: 2024, cents: 7.5 },
]

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const GOLD_LIGHT = '#D4A017'
const MUTED = '#444'
const TEXT = '#ccc'
const CHART_BG = '#111'

const PIE_COLORS = ['#8B1A1A', '#B8860B', '#6B3D3D', '#7A6A00', '#3a3a3a']

const S = {
  shell: { color: '#e0e0e0', fontFamily: "'DM Sans', sans-serif" },
  statsRow: { display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' },
  statCard: {
    background: '#111',
    border: '1px solid #1e1e1e',
    borderRadius: '8px',
    padding: '1.25rem 1.5rem',
    flex: '1',
    minWidth: '180px',
  },
  statValue: { fontSize: '1.9rem', fontWeight: 700, color: GOLD, lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: '#bbb', marginTop: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  chartCard: {
    background: CHART_BG,
    border: '1px solid #1e1e1e',
    borderRadius: '8px',
    padding: '1.25rem',
  },
  chartTitle: { fontSize: '0.82rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem', fontWeight: 600 },
  callout: {
    background: '#0d0505',
    border: `1px solid ${BURGUNDY}`,
    borderLeft: `4px solid ${BURGUNDY}`,
    borderRadius: '8px',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  calloutHeadline: { fontSize: '1.5rem', fontWeight: 800, color: '#e0e0e0', lineHeight: 1.25, marginBottom: '0.75rem' },
  calloutStat: { fontSize: '2.8rem', fontWeight: 900, color: BURGUNDY, lineHeight: 1, marginBottom: '0.25rem' },
  calloutSub: { fontSize: '0.85rem', color: '#bbb', lineHeight: 1.5 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1.5rem' },
  th: {
    textAlign: 'left', padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #4a9eff',
    color: '#4a9eff', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #1a1a1a', color: TEXT },
  tdHighlight: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #1a1a1a', color: GOLD, fontWeight: 700 },
  bar: { height: '6px', background: '#1a1a1a', borderRadius: '3px', overflow: 'hidden', marginTop: '4px' },
  barFill: { height: '100%', background: BURGUNDY, borderRadius: '3px' },
  sourceNote: { fontSize: '0.72rem', color: '#bbb', borderTop: '1px solid #1a1a1a', paddingTop: '1rem', marginTop: '0.5rem' },
}

const customTooltipStyle = {
  background: '#181818',
  border: '1px solid #2a2a2a',
  borderRadius: '6px',
  fontSize: '0.82rem',
  color: '#ccc',
}

function ChartTooltip({ active, payload, label, unit = '' }) {
  if (!active || !payload?.length) return null
  return (
    <div style={customTooltipStyle}>
      <div style={{ padding: '0.6rem 0.8rem' }}>
        <div style={{ color: '#bbb', marginBottom: '0.3rem', fontSize: '0.75rem' }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color || GOLD }}>
            {p.name}: <strong>{p.value}{unit}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function PieLabelLine({ cx, cy, midAngle, innerRadius, outerRadius, pct, country }) {
  const RADIAN = Math.PI / 180
  const r = outerRadius + 20
  const x = cx + r * Math.cos(-midAngle * RADIAN)
  const y = cy + r * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill={TEXT} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
      {country} {pct}%
    </text>
  )
}

export default function TradeAnalysis() {
  return (
    <div style={S.shell}>
      {/* Stat cards */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statValue}>$248M</div>
          <div style={S.statLabel}>Total 2024 Exports</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>186,000 MT</div>
          <div style={S.statLabel}>Total 2024 Volume</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>$0.075</div>
          <div style={S.statLabel}>Avg. Price Per Fish</div>
        </div>
      </div>

      {/* Row 1: Pie + Bar */}
      <div style={S.row}>
        {/* Pie chart */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>2024 Export Destinations by Volume</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={exportDestinations}
                cx="50%"
                cy="50%"
                outerRadius={90}
                dataKey="pct"
                labelLine={false}
                label={({ cx, cy, midAngle, outerRadius, pct, country }) => {
                  const RADIAN = Math.PI / 180
                  const r = outerRadius + 28
                  const x = cx + r * Math.cos(-midAngle * RADIAN)
                  const y = cy + r * Math.sin(-midAngle * RADIAN)
                  return (
                    <text x={x} y={y} fill="#aaa" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
                      {country} {pct}%
                    </text>
                  )
                }}
              >
                {exportDestinations.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i]} stroke="#111" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const d = payload[0].payload
                  return (
                    <div style={{ ...customTooltipStyle }}>
                      <div style={{ padding: '0.6rem 0.8rem' }}>
                        <div style={{ color: GOLD, fontWeight: 700, marginBottom: '0.3rem' }}>{d.country}</div>
                        <div>Volume: {d.volumeMT.toLocaleString()} MT</div>
                        <div>Value: ${d.valueMillion}M</div>
                        <div>Share: {d.pct}%</div>
                      </div>
                    </div>
                  )
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Historical Export Value (2019–2024)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={historicalExports} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" />
              <XAxis dataKey="year" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}M`} />
              <Tooltip content={<ChartTooltip unit="M" />} />
              <Bar dataKey="valueMillion" name="Export Value" fill={BURGUNDY} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Line + Callout */}
      <div style={S.row}>
        {/* Line chart */}
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Price Per Fish Over Time (cents)</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={pricePerFish} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" />
              <XAxis dataKey="year" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}¢`} domain={[5, 8]} />
              <Tooltip content={<ChartTooltip unit="¢" />} />
              <Line
                type="monotone"
                dataKey="cents"
                name="Price"
                stroke={GOLD_LIGHT}
                strokeWidth={2.5}
                dot={{ fill: GOLD_LIGHT, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Stark callout card */}
        <div style={S.callout}>
          <div style={S.calloutStat}>7.5¢</div>
          <div style={S.calloutHeadline}>Less than a gumball.</div>
          <div style={S.calloutSub}>
            At 7.5 cents per fish, American menhaden are worth less than a stick of gum.
            <br /><br />
            <strong style={{ color: GOLD }}>China receives 48%</strong> of all US menhaden exports —
            nearly half of every fish ground up and shipped overseas.
            <br /><br />
            Meanwhile, the ecosystems that depend on healthy menhaden populations —
            striped bass, tuna, whales, osprey — collapse in silence.
          </div>
        </div>
      </div>

      {/* Data table */}
      <div style={{ ...S.chartCard, marginBottom: '1.25rem' }}>
        <div style={S.chartTitle}>2024 Export Destinations — Full Breakdown</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Destination</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Volume (MT)</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Value ($M)</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Share</th>
              <th style={{ ...S.th, width: '120px' }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {exportDestinations.map((row, i) => (
              <tr key={row.country}>
                <td style={i === 0 ? S.tdHighlight : S.td}>{row.country}</td>
                <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.volumeMT.toLocaleString()}</td>
                <td style={{ ...S.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${row.valueMillion}M</td>
                <td style={{ ...S.td, textAlign: 'right', color: GOLD }}>{row.pct}%</td>
                <td style={S.td}>
                  <div style={S.bar}>
                    <div style={{ ...S.barFill, width: `${row.pct}%`, background: PIE_COLORS[i] }} />
                  </div>
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ ...S.td, fontWeight: 700, color: '#e0e0e0', borderTop: '1px solid #2a2a2a' }}>Total</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#e0e0e0', borderTop: '1px solid #2a2a2a', fontVariantNumeric: 'tabular-nums' }}>186,000</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#e0e0e0', borderTop: '1px solid #2a2a2a' }}>$248M</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#e0e0e0', borderTop: '1px solid #2a2a2a' }}>100%</td>
              <td style={{ ...S.td, borderTop: '1px solid #2a2a2a' }} />
            </tr>
          </tbody>
        </table>
        <div style={S.sourceNote}>
          Source: US Census Bureau trade data, NOAA fisheries statistics
        </div>
      </div>
    </div>
  )
}
